import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useEffect, useState} from 'react';
import {usePrevious} from 'utils/hooks';
import {useNetworkHeartbeat} from 'APIs/NetworkStatus/heartbeat';
import useTrackEvent from 'utils/hooks/useTrackEvent';
import {EventName} from 'utils/hooks/useTrackEvent/types';
import {
  requestToEventData,
  SyncRequest,
} from '../../../utils/functions/requestToEventData';
import styles from './AppStatusBar.module.scss';

type AppStatus = 'online' | 'offline';

enum NetworkStatusIcon {
  'syncInProgress' = 'refresh',
  'syncSuccess' = 'check',
  'syncFailed' = 'times',
}

export interface SyncMessage {
  status: 'syncInProgress' | 'syncSuccess' | 'syncFailed';
  message: string;
  progress?: number;
  requestBody?: string;
  requestUrl?: string;
}

function AppStatusBar(): JSX.Element {
  const [appStatus, setAppStatus] = useState<AppStatus>();
  const [syncMessage, setSyncMessage] = useState<SyncMessage>();
  const {trackEvent} = useTrackEvent();

  useNetworkHeartbeat();
  const previousAppStatus = usePrevious(appStatus);
  const syncChannel = new BroadcastChannel('sync-progress');

  useEffect(() => {
    syncChannel.addEventListener('message', getSyncMessage);
    window.addEventListener('networkStatus', (event) => updateAppStatus(event));

    return () => {
      syncChannel.removeEventListener('message', getSyncMessage);
      window.removeEventListener('networkStatus', (event) =>
        updateAppStatus(event),
      );
    };
  }, []);

  useEffect(() => {
    // manually sync queued requests on status change
    if (appStatus === 'online' && previousAppStatus !== 'online') {
      navigator.serviceWorker?.ready
        ?.then((registration) => {
          registration.active?.postMessage({type: 'REPLAY_SYNC'});
        })
        .catch((error) => {
          console.log('Service Worker could not replay sync requests:', error);
        });
    }
  }, [appStatus]);

  function getSyncMessage(event: MessageEvent<SyncMessage>) {
    setSyncMessage(event.data);

    if (event?.data?.requestBody || event?.data?.requestUrl) {
      const {requestBody, requestUrl} = event.data;
      trackBackgroundSyncRequest('offline_app_sync_sucess', {
        body: requestBody ?? '',
        url: requestUrl ?? '',
      });
    }

    if (event?.data?.status === 'syncSuccess') {
      setTimeout(() => setSyncMessage(undefined), 3000);
    }
  }

  function trackBackgroundSyncRequest(
    eventName: EventName,
    {body, url}: SyncRequest,
  ) {
    const eventData = requestToEventData({body, url});

    trackEvent(eventName, eventData);
  }

  function updateAppStatus(event: CustomEvent<AppStatus>) {
    setAppStatus(event.detail);
  }

  return (
    <div className={styles.wrapper}>
      {syncMessage ? (
        <div className={`${styles.warning} ${styles[syncMessage.status]}`}>
          <span>
            {syncMessage.message}
            {syncMessage.status !== 'syncFailed'
              ? ` (${syncMessage.progress?.toFixed(0)}%)`
              : ''}
          </span>
          <FontAwesomeIcon
            icon={NetworkStatusIcon[syncMessage.status]}
            size="lg"
          />
        </div>
      ) : (
        <div className={`${styles.warning} ${styles[appStatus ?? 'offline']}`}>
          <span>{appStatus ?? 'offline'}</span>

          <FontAwesomeIcon
            size="sm"
            icon={appStatus === 'offline' ? 'wifi-slash' : 'wifi'}
          />
        </div>
      )}
    </div>
  );
}

export default AppStatusBar;
